/**
 * Project tools sub-views (handoff §4 "Project tools"): Time, Project settings
 * and Data & lifecycle. AI Co-Pilot and Automations keep their own components.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    getDeletedTasks, restoreTask, updateProject, deleteProject,
    type Project, type Task,
} from '../../../packages/api-client/pm-api';
import { getActiveTimer, type TimerSession } from '../../../packages/api-client/timetracking-api';
import { getErrorMessage } from '../../../packages/api-client';
import { ProjectTypeField } from '../../../components/ProjectTypeField';
import { Dialog, StateView, Table } from '../../../packages/ui';

const STATUS_OPTIONS: Array<[Project['status'], string]> = [
    ['planning', 'Planning'], ['active', 'Active'], ['on_hold', 'On Hold'], ['completed', 'Completed'], ['archived', 'Archived'],
];

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

// ─── Time ────────────────────────────────────────────────────────────────────

/**
 * Time is logged per task (Task detail › Time). There is no project time
 * endpoint, so this view shows the caller's running timer and says so rather
 * than summing entries client-side.
 */
export function TimeTool() {
    const [timer, setTimer] = useState<TimerSession | null | undefined>(undefined);
    const [error, setError] = useState('');
    useEffect(() => {
        getActiveTimer().then(setTimer).catch(e => setError(getErrorMessage(e)));
    }, []);

    return (
        <div className="project-tool">
            <h3 className="project-tool-title">Your running timer</h3>
            {error ? <StateView state="error" message={error} />
                : timer === undefined ? <StateView state="loading" label="Loading timer" lines={1} />
                : timer === null ? <p className="project-tool-muted">No timer running</p>
                : <p>Running on <Link to={`/tasks/${timer.task_id}`}>{timer.task_title || 'a task'}</Link> since {when(timer.started_at)}.</p>}
            <p className="project-tool-muted">
                Time is logged on each task, in its Time section (timer or manual entry). KaziAtlas has
                no project-level timesheet yet, so hours are not totalled here.
            </p>
        </div>
    );
}

// ─── Project settings ────────────────────────────────────────────────────────

/**
 * Edits what PUT /projects/{id} accepts. Sends the fields the Projects page's
 * edit modal sends (name, description, status, icon; color only when changed,
 * so an unset color is never stamped with the picker's default) plus repo_url
 * and the dates; an empty repo_url or date clears it server-side.
 */
export function ProjectSettings({ project, onProjectChange }: { project: Project; onProjectChange: (p: Project) => void }) {
    const [form, setForm] = useState(() => ({
        project_type: project.project_type ?? 'software',
        name: project.name,
        description: project.description ?? '',
        status: project.status,
        color: project.color ?? '',
        repo_url: project.repo_url ?? '',
        start_date: project.start_date?.slice(0, 10) ?? '',
        due_date: project.due_date?.slice(0, 10) ?? '',
    }));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setSaved(false);
        setForm(f => ({ ...f, [k]: e.target.value }));
    };

    async function save(e: React.FormEvent) {
        e.preventDefault();
        setSaving(true); setError(''); setSaved(false);
        try {
            const updated = await updateProject(project.id, {
                name: form.name, description: form.description, status: form.status,
                icon: project.icon,
                ...(form.color !== (project.color ?? '') && { color: form.color }),
                project_type: form.project_type,
                ...(form.project_type === 'software' && { repo_url: form.repo_url }),
                start_date: form.start_date, due_date: form.due_date,
            });
            onProjectChange({ ...project, ...updated });
            setSaved(true);
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <form className="project-tool project-settings" onSubmit={save}>
            <h3 className="project-tool-title">Project settings</h3>
            <label className="form-label" htmlFor="ps-name">Name</label>
            <input id="ps-name" className="form-input" required value={form.name} onChange={set('name')} />
            <label className="form-label" htmlFor="ps-desc">Description</label>
            <textarea id="ps-desc" className="form-input" rows={3} value={form.description} onChange={set('description')} />
            <ProjectTypeField id="ps-type" value={form.project_type} onChange={project_type => { setSaved(false); setForm(f => ({ ...f, project_type })); }} />
            {form.project_type === 'software' && <>
                <label className="form-label" htmlFor="ps-repo">Repository URL</label>
                <input id="ps-repo" className="form-input" type="url" placeholder="https://github.com/owner/repo" value={form.repo_url} onChange={set('repo_url')} />
            </>}
            <div className="project-settings-row">
                <div>
                    <label className="form-label" htmlFor="ps-status">Status</label>
                    <select id="ps-status" className="form-input" value={form.status} onChange={set('status')}>
                        {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </div>
                <div>
                    <label className="form-label" htmlFor="ps-start">Start date</label>
                    <input id="ps-start" className="form-input" type="date" value={form.start_date} onChange={set('start_date')} />
                </div>
                <div>
                    <label className="form-label" htmlFor="ps-due">Due date</label>
                    <input id="ps-due" className="form-input" type="date" value={form.due_date} onChange={set('due_date')} />
                </div>
                <div>
                    <label className="form-label" htmlFor="ps-color">Color</label>
                    <input id="ps-color" className="form-input" type="color" value={form.color || '#14867a'} onChange={set('color')} />
                </div>
            </div>
            {error && <p role="alert" className="form-error">{error}</p>}
            <div className="project-tool-actions">
                {saved && <span role="status">Saved</span>}
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving} aria-busy={saving || undefined}>Save settings</button>
            </div>
        </form>
    );
}

// ─── Data & lifecycle ────────────────────────────────────────────────────────

type Confirm = 'archive' | 'reactivate' | 'delete' | null;

export function DataLifecycle({ project, onProjectChange }: { project: Project; onProjectChange: (p: Project) => void }) {
    const navigate = useNavigate();
    const [confirm, setConfirm] = useState<Confirm>(null);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState('');
    const archived = project.status === 'archived';

    const close = () => { setConfirm(null); setActionError(''); };
    async function run() {
        setBusy(true); setActionError('');
        try {
            if (confirm === 'delete') {
                await deleteProject(project.id);
                navigate('/projects/deleted');
                return;
            }
            const updated = await updateProject(project.id, { status: confirm === 'archive' ? 'archived' : 'active' });
            onProjectChange({ ...project, ...updated });
            setConfirm(null);
        } catch (err) {
            setActionError(getErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }

    const copy = {
        archive: { title: `Archive ${project.name}?`, verb: 'Archive', body: 'The project is marked Archived. Its tasks and history stay as they are, and you can reactivate it here.' },
        reactivate: { title: `Reactivate ${project.name}?`, verb: 'Reactivate', body: 'The project goes back to Active.' },
        delete: { title: `Delete ${project.name}?`, verb: 'Delete', body: "The project disappears from Projects for everyone, and its tasks and milestones are deleted with it. Workspace admins and the project's owner or members can restore the project from Deleted projects (restricted users may not see that page). Restoring the project does not bring back its tasks: restore them separately here, in Data & lifecycle. Milestones can't currently be restored from the app." },
    };
    const c = confirm && copy[confirm];

    return (
        <div className="project-tool">
            <DeletedTasks projectId={project.id} />

            <h3 className="project-tool-title">Project lifecycle</h3>
            <div className="project-tool-actions project-tool-actions--start">
                {archived
                    ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirm('reactivate')}>Reactivate project</button>
                    : <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirm('archive')}>Archive project</button>}
                <button type="button" className="btn btn-danger btn-sm" onClick={() => setConfirm('delete')}>Delete project</button>
                <Link to="/projects/deleted">Deleted projects</Link>
            </div>

            <Dialog open={!!c} onClose={close} title={c?.title} closeOnBackdrop={false}
                footer={<>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={close}>Cancel</button>
                    <button type="button" className={`btn btn-sm ${confirm === 'delete' ? 'btn-danger' : 'btn-primary'}`} onClick={run} disabled={busy} aria-busy={busy || undefined}>{c?.verb}</button>
                </>}>
                <p>{c?.body}</p>
                {actionError && <p role="alert" className="form-error">{actionError}</p>}
            </Dialog>
        </div>
    );
}

/** Soft-deleted tasks the caller may see (the endpoint applies restriction). */
function DeletedTasks({ projectId }: { projectId: string }) {
    const [tasks, setTasks] = useState<Task[] | null>(null);
    const [loadError, setLoadError] = useState('');
    const [rowError, setRowError] = useState('');
    const [restored, setRestored] = useState<Task | null>(null);
    const [pending, setPending] = useState<string | null>(null);

    // ponytail: first 50 only; add paging when a project routinely has more deleted tasks.
    const load = useCallback(() => {
        setLoadError(''); setTasks(null);
        getDeletedTasks(projectId).then(r => setTasks(r.data ?? [])).catch(e => setLoadError(getErrorMessage(e)));
    }, [projectId]);
    useEffect(load, [load]);

    async function restore(t: Task) {
        setPending(t.id); setRowError(''); setRestored(null);
        try {
            await restoreTask(t.id);
            setTasks(prev => (prev ?? []).filter(x => x.id !== t.id));
            setRestored(t);
        } catch (err) {
            setRowError(`Couldn't restore "${t.title}": ${getErrorMessage(err)}`);
        } finally {
            setPending(null);
        }
    }

    return (
        <>
            <h3 className="project-tool-title">Deleted tasks</h3>
            {restored && (
                <p role="status">
                    Restored <Link to={`/tasks/${restored.id}`}>{restored.title}</Link>. It is back in{' '}
                    <Link to={`/projects/${projectId}?tab=Tasks`}>Tasks</Link>.
                </p>
            )}
            {rowError && <p role="alert" className="form-error">{rowError}</p>}
            {loadError ? <StateView state="error" title="Deleted tasks couldn't be loaded" message={loadError} onRetry={load} />
                : tasks === null ? <StateView state="loading" label="Loading deleted tasks" />
                : tasks.length === 0 ? <StateView state="empty" title="No deleted tasks" message="Tasks deleted from this project appear here and can be restored." />
                : (
                    <Table caption="Deleted tasks" hideCaption density="compact" rowKey={r => r.id as string}
                        data={tasks as unknown as Record<string, unknown>[]}
                        columns={[
                            { key: 'title', header: 'Task', rowHeader: true },
                            { key: 'deleted_at', header: 'Deleted', render: r => when(r.deleted_at as string) },
                            { key: 'restore', header: 'Action', render: r => {
                                const t = r as unknown as Task;
                                return <button type="button" className="btn btn-secondary btn-sm" aria-label={`Restore ${t.title}`}
                                    onClick={() => restore(t)} disabled={pending === t.id}>Restore</button>;
                            } },
                        ]} />
                )}
        </>
    );
}
